import { useMemo } from 'react';
import { useQueries } from '@tanstack/react-query';
import { isConfigApiError } from '@web-config-app/core';
import type {
  DomainEntity,
  EntityStatus,
  EnvironmentKey,
} from '@web-config-app/core';
import { getEntityGetQueryConfig } from '../use-entity-get/entity-get-query';
import { useEnvironmentFetchFactory } from '../use-environment-fetch/use-environment-fetch';

/**
 * `isResolved` separates "we know" from "we don't", which `status` alone can't -- both are
 * undefined. Anything but a 404 leaves the lookup unresolved.
 */
export interface EntityStatusInEnvironment {
  status?: EntityStatus;
  readOnly?: boolean;
  isResolved: boolean;
}

/**
 * This only feeds menu wording and a badge, and the answer changes when someone promotes, not
 * second to second. Without it these inherit the app default of 0 and refetch on every mount
 * and refocus, once per environment.
 */
const STATUS_STALE_TIME_MS = 60_000;

const HTTP_NOT_FOUND = 404;

/**
 * Only a 404 answers the question. Calling any other failure "absent" would offer Copy for an
 * entity that may already be there, mid-outage.
 */
const isEntityAbsent = (error: unknown) =>
  isConfigApiError(error) && error.status === HTTP_NOT_FOUND;

/**
 * Looks up an entity's status in several environments at once.
 *
 * Status only, not included entities -- the with-includes payload is expensive and callers
 * just need to know whether the entity is there. Keys come from getEntityGetQueryConfig, so
 * this and a status-only useEntityGet share a cache entry by construction.
 */
export const useEntityStatusesByEnvironment = ({
  entity,
  instanceId,
  environments,
}: {
  entity: DomainEntity;
  instanceId?: string;
  environments: EnvironmentKey[];
}): Partial<Record<EnvironmentKey, EntityStatusInEnvironment>> => {
  const createEnvironmentFetch = useEnvironmentFetchFactory();

  const statuses = useQueries({
    queries: environments.map((environment) => ({
      ...getEntityGetQueryConfig({
        entity,
        instanceId,
        environment,
        environmentFetch: createEnvironmentFetch(environment, entity.apiUrl),
      }),
      enabled: Boolean(instanceId),
      retry: false,
      staleTime: STATUS_STALE_TIME_MS,
      refetchOnWindowFocus: false,
    })),
    // `combine` keeps the derivation in one place instead of mapping the results array by
    // hand. It does not keep the identity stable here, though -- see below.
    combine: (results) =>
      Object.fromEntries(
        environments.map((environment, index) => {
          const result = results[index];
          const metadata = result?.data?.data?.attributes?.entityMetadata;
          return [
            environment,
            {
              isResolved: Boolean(
                result?.isSuccess ||
                  (result?.isError && isEntityAbsent(result.error)),
              ),
              // Read only on success: React Query keeps the last `data` alongside `isError`
              // when a refetch fails, and that stale payload must not look current.
              status: result?.isSuccess ? metadata?.status : undefined,
              readOnly: result?.isSuccess ? metadata?.readOnly : undefined,
            },
          ];
        }),
      ),
  });

  // query-core 5.28.x declares its #combinedResult field but never assigns it, so combine's
  // replaceEqualDeep always compares against undefined and hands back a new object every
  // render. Identity matters here: this flows through the details container into the entity
  // details context value, so an unstable result re-renders the whole form tree. Re-stabilise
  // on a signature of the combined value rather than on the results array, which useQueries
  // rebuilds regardless. Revisit if the library fixes the assignment.
  const signature = JSON.stringify(statuses);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  return useMemo(() => statuses, [signature]);
};
